import React, { useRef } from 'react';
import {
    applyToValue, autoFormat, breakInItem, continueList, removeMark, shiftLevel, toggleList, typeEdit,
} from '../utils/bulletText';
import '../styles/BulletTextarea.css';

const plain = e => !e.ctrlKey && !e.metaKey && !e.altKey;
// Keeps the focus, and with it the selection, in the textarea.
const keepFocus = e => e.preventDefault();

/** A textarea that writes bullet lists: "- " starts one, Enter continues it, Tab nests it. */
export default function BulletTextarea({ id, label, value, onChange, rows = 6, hint = false }) {
    const ref = useRef(null);

    const apply = edit => {
        const ta = ref.current;
        ta.focus();
        if (ta.value.slice(edit.from, edit.to) !== edit.text && !typeEdit(ta, edit)) {
            // Assigned before onChange, so React finds the value unchanged and leaves the caret.
            const next = applyToValue(ta.value, edit);
            ta.value = next;
            onChange(next);
        }
        ta.setSelectionRange(edit.selStart, edit.selEnd);
    };

    const state = () => {
        const ta = ref.current;
        return { value: ta.value, start: ta.selectionStart, end: ta.selectionEnd };
    };

    const onKeyDown = e => {
        if (e.nativeEvent.isComposing || !plain(e)) return;
        let edit = null;
        if (e.key === 'Enter') edit = e.shiftKey ? breakInItem(state()) : continueList(state());
        else if (e.key === 'Tab') edit = shiftLevel(state(), e.shiftKey ? -1 : 1);
        else if (e.key === ' ' && !e.shiftKey) edit = autoFormat(state());
        else if (e.key === 'Backspace' && !e.shiftKey) edit = removeMark(state());
        if (!edit) return;
        e.preventDefault();
        apply(edit);
    };

    const tool = (name, title, run) => (
        <button type="button" className="inv-btn bt-tool" title={title} aria-label={title}
            onMouseDown={keepFocus}
            onClick={() => { const edit = run(state()); if (edit) apply(edit); }}>
            {name}
        </button>
    );

    return (
        <>
            <div className="bt-head">
                <label className="inv-label" htmlFor={id}>{label}</label>
                <div className="bt-tools" role="toolbar" aria-label={`${label}: Aufzählung`}>
                    {tool('• Liste', 'Aufzählung ein/aus', toggleList)}
                    {tool('⇤', 'Ausrücken (Shift+Tab)', s => shiftLevel(s, -1))}
                    {tool('⇥', 'Einrücken (Tab)', s => shiftLevel(s, 1))}
                </div>
            </div>
            <textarea id={id} ref={ref} className="inv-textarea bt-textarea" rows={rows}
                value={value} onChange={e => onChange(e.target.value)} onKeyDown={onKeyDown} />
            {hint && (
                <p className="bt-hint">
                    „- “ beginnt eine Aufzählung, Enter den nächsten Punkt, Tab einen Unterpunkt.
                    Enter in einem leeren Punkt beendet die Liste.
                </p>
            )}
        </>
    );
}
